// Route tester — the Google Maps side. Icons, the room-number overlay class,
// the polygon palette and the little geometry the hit-testing needs. Every
// function that constructs a Maps object checks `hasMaps()` first, so the
// component renders (and the Jest suite runs) with no Maps script at all.

/** True when the Maps JS API is on the page. */
export function hasMaps() {
  return typeof window !== 'undefined' && !!window.google?.maps?.Map;
}

// Same palette /umanitoba renders buildings with (src/pathfinder/useRouteLayer.js
// POLY_COLORS). Copied, not imported — the pathfinder folder is copy-out clean
// and its internals are not an export surface. ONLY structure polygons here:
// no places, bus stops, parking, events.
export const POLY_COLORS = {
  outline:  { fill: '#ffffff', fillOp: 1,    stroke: '#aaaaaa', strokeW: 0   },
  room:     { fill: '#FCF8EF', fillOp: 0.85, stroke: '#dbdbdb', strokeW: 1.5 },
  stair:    { fill: '#c4956a', fillOp: 0.9,  stroke: '#a0724a', strokeW: 1.5 },
  elevator: { fill: '#b0bec5', fillOp: 0.9,  stroke: '#78909c', strokeW: 1.5 },
  blocked:  { fill: '#000000', fillOp: 0.9,  stroke: '#000000', strokeW: 1.5 },
  tunnel:   { fill: '#ffffff', fillOp: 1,    stroke: '#aaaaaa', strokeW: 0   },
};
// A footprint with nothing drawn inside it yet (zoomed out, or not fetched)
// is drawn the way /umanitoba draws an idle building, so campus reads the same.
export const FOOTPRINT = { fill: '#57b4f5', fillOp: 0.5, stroke: '#0d3b66', strokeW: 1.5 };
export const ROOM_HOVER = { fill: '#fbbc04', fillOp: 0.55, stroke: '#f29900', strokeW: 2.5 };

/** The pill marker /umanitoba uses for Start and End (useRouteLayer's roomLabelIcon). */
export function pillIcon(text, color) {
  if (!hasMaps()) return null;
  const raw = String(text ?? '');
  const MAX = 26;
  let display = raw;
  if (raw.length > MAX) {
    const parts = raw.split(/\s+/);
    const tail = parts.length > 1 ? parts[parts.length - 1] : '';
    if (tail && /\d/.test(tail)) {
      const head = raw.slice(0, raw.length - tail.length).trim();
      const keep = Math.max(3, MAX - tail.length - 2);
      display = (head.length > keep ? head.slice(0, keep) + '…' : head) + ' ' + tail;
    } else {
      display = raw.slice(0, MAX - 1) + '…';
    }
  }
  const esc = display.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const w = Math.min(Math.max(64, display.length * 6.5 + 18), 200);
  const h = 26;
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h + 7}">`,
    `<rect x="1" y="1" width="${w - 2}" height="${h - 2}" rx="6" fill="${color}"/>`,
    `<text x="${w / 2}" y="${h / 2 + 4}" font-family="-apple-system,BlinkMacSystemFont,sans-serif"`,
    ` font-size="10.5" font-weight="700" fill="#fff" text-anchor="middle">${esc}</text>`,
    `<polygon points="${w / 2 - 4},${h - 1} ${w / 2 + 4},${h - 1} ${w / 2},${h + 6}" fill="${color}"/>`,
    `</svg>`,
  ].join('');
  return {
    url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(w, h + 7),
    anchor: new window.google.maps.Point(w / 2, h + 7),
  };
}

/** A small filled circle; `mark` ("✕", "⚠") is drawn inside a bigger one. */
export function nodeIcon(color, { scale = 4.5, opacity = 1, mark = '', hatched = false, ring = '#ffffff' } = {}) {
  if (!hasMaps()) return null;
  if (!mark && !hatched) {
    return {
      path: window.google.maps.SymbolPath.CIRCLE, scale,
      fillColor: color, fillOpacity: opacity, strokeColor: ring, strokeWeight: 1.5, strokeOpacity: opacity,
    };
  }
  const r = 8;
  const d = r * 2 + 4;
  const fill = hatched ? 'url(#h)' : color;
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${d}" height="${d}" viewBox="0 0 ${d} ${d}">`,
    hatched
      ? `<defs><pattern id="h" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="4" height="4" fill="#cfd8dc"/><rect width="2" height="4" fill="#607d8b"/></pattern></defs>`
      : '',
    `<circle cx="${d / 2}" cy="${d / 2}" r="${r}" fill="${fill}" fill-opacity="${opacity}" stroke="${ring}" stroke-width="2"/>`,
    mark ? `<text x="${d / 2}" y="${d / 2 + 4}" font-family="-apple-system,sans-serif" font-size="11" font-weight="800" fill="#fff" text-anchor="middle">${mark}</text>` : '',
    `</svg>`,
  ].join('');
  return {
    url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(d, d),
    anchor: new window.google.maps.Point(d / 2, d / 2),
  };
}

/**
 * Polyline options for a variant's dash pattern. Maps draws dashes as repeated
 * symbols over an invisible stroke, so dashed and dotted lines set the real
 * stroke to 0 and carry the colour in `icons`.
 */
export function lineStyle(dash, color, weight, opacity) {
  if (!hasMaps()) return {};
  if (dash === 'dashed') {
    return {
      strokeOpacity: 0,
      icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: opacity, strokeColor: color, strokeWeight: weight, scale: 4 }, offset: '0', repeat: '16px' }],
    };
  }
  if (dash === 'dotted') {
    return {
      strokeOpacity: 0,
      icons: [{ icon: { path: window.google.maps.SymbolPath.CIRCLE, fillOpacity: opacity, fillColor: color, strokeOpacity: 0, scale: weight * 0.55 }, offset: '0', repeat: `${weight * 2.2}px` }],
    };
  }
  return { strokeOpacity: opacity, strokeColor: color, strokeWeight: weight };
}

let RoomLabelClass = null;
/**
 * The room-number overlay (EngTourCreator's RoomLabel): a div in the float
 * pane, font `(zoom-16)*4` clamped 9–20. Built lazily because the base class
 * only exists once the Maps script has loaded.
 */
export function roomLabelClass() {
  if (!hasMaps()) return null;
  if (RoomLabelClass) return RoomLabelClass;
  RoomLabelClass = class RoomLabel extends window.google.maps.OverlayView {
    constructor(position, text, zoom) {
      super();
      this.position = position; this.text = text; this.zoom = zoom; this._div = null;
    }
    onAdd() {
      const d = document.createElement('div');
      d.textContent = this.text;
      d.style.cssText = [
        'position:absolute', 'z-index:1', 'transform:translate(-50%,-50%)',
        'background:rgba(255,255,255,0.9)', 'border-radius:4px',
        'padding:2px 6px', 'font-family:Arial,sans-serif', 'font-weight:700',
        'color:#111', 'white-space:nowrap', 'pointer-events:none',
        'user-select:none', 'box-shadow:0 1px 5px rgba(0,0,0,0.25)',
      ].join(';');
      this._div = d;
      // markerLayer, not floatPane: the pane's children stack by z-index, so
      // at z-index 1 a room number sits above the polygons (overlayLayer is
      // below this pane) and under every marker — the Start/End pills and
      // the route nodes are never covered by a label.
      this.getPanes().markerLayer.appendChild(d);
      this.applyZoom(this.zoom);
    }
    applyZoom(z) {
      if (!this._div || z == null) return;
      this._div.style.fontSize = Math.max(9, Math.min(20, Math.round((z - 16) * 4))) + 'px';
    }
    draw() {
      const pos = this.getProjection()?.fromLatLngToDivPixel(this.position);
      if (pos && this._div) { this._div.style.left = pos.x + 'px'; this._div.style.top = pos.y + 'px'; }
    }
    onRemove() { if (this._div) { this._div.parentNode?.removeChild(this._div); this._div = null; } }
  };
  return RoomLabelClass;
}

// ── Geometry (plain numbers, no Maps) ────────────────────────────────────────

export function ringBounds(ring) {
  let s = Infinity, n = -Infinity, w = Infinity, e = -Infinity;
  for (const p of ring) {
    if (p.lat < s) s = p.lat; if (p.lat > n) n = p.lat;
    if (p.lng < w) w = p.lng; if (p.lng > e) e = p.lng;
  }
  return { s, n, w, e };
}

export function boundsOfRings(rings) {
  let s = Infinity, n = -Infinity, w = Infinity, e = -Infinity;
  for (const ring of rings) {
    const b = ringBounds(ring);
    if (b.s < s) s = b.s; if (b.n > n) n = b.n; if (b.w < w) w = b.w; if (b.e > e) e = b.e;
  }
  return Number.isFinite(s) ? { s, n, w, e } : null;
}

export function boxesIntersect(a, b) {
  return !!a && !!b && a.s <= b.n && a.n >= b.s && a.w <= b.e && a.e >= b.w;
}

export function inBox(pt, b) {
  return !!b && pt.lat >= b.s && pt.lat <= b.n && pt.lng >= b.w && pt.lng <= b.e;
}

export function ringCentroid(ring) {
  let lat = 0, lng = 0;
  for (const p of ring) { lat += p.lat; lng += p.lng; }
  return ring.length ? { lat: lat / ring.length, lng: lng / ring.length } : null;
}

/** Ray-cast point-in-polygon on {lat,lng} rings. */
export function pointInRing(pt, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    const hit = ((a.lat > pt.lat) !== (b.lat > pt.lat)) &&
      (pt.lng < ((b.lng - a.lng) * (pt.lat - a.lat)) / ((b.lat - a.lat) || 1e-12) + a.lng);
    if (hit) inside = !inside;
  }
  return inside;
}

/** google.maps.LatLngBounds → the plain box the geometry above uses. */
export function boxOf(bounds) {
  if (!bounds) return null;
  const sw = bounds.getSouthWest(), ne = bounds.getNorthEast();
  return { s: sw.lat(), n: ne.lat(), w: sw.lng(), e: ne.lng() };
}
